import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import LabelSelect from 'src/modules/shared/components/atoms/inputs/label-select';
import LabelNumberInput from 'src/modules/shared/components/atoms/inputs/label-number-input';
import LabelSwitch from 'src/modules/shared/components/atoms/inputs/label-switch';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import type { IntegrationConnectionUiStatus } from 'src/modules/shared/types/integration-connection-status.type';
import { IntegrationPermission } from 'src/modules/shared/types/permissions.type';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useAlert } from 'src/contexts/alert-context';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useNavigate } from '@tanstack/react-router';
import { Route as SettingsCredentialsRoute } from 'src/routes/settings/credentials';
import { settingsSearchSchema } from 'src/modules/settings/schemas/settings.schema';
import { useCredentials } from 'src/modules/shared/hooks/api/credentials/credentials';
import { extractApiErrorMessage } from 'src/modules/shared/utils/api/error-handler.util';
import {
  useConnectIntegration,
  useDisconnectIntegration,
  useEnqueueIntegrationSync,
  useIntegrations,
} from 'src/modules/shared/hooks/api/integrations/integrations';
import { PiArrowClockwise, PiLinkBreak, PiLink, PiPlus } from 'react-icons/pi';
import {
  type IntegrationProviderId,
  TCPropertyImportPropertyScope,
  TCPropertyImportSyncMissingPolicy,
  type TCCredential,
  TCCredentialKind,
  TCCredentialStatus,
  IntegrationConnectionHealthStatus,
  IntegrationConnectionStatus,
} from 'src/api/generated';
import { getIntegrationMinSyncFrequencyMinutes } from 'src/modules/settings/constants/integration-sync.constants';
import {
  formatIntegrationDateTime,
  formatIntegrationNextSyncDateTime,
} from 'src/modules/settings/utils/integration-date.util';
import { IntegrationConnectionBadges } from 'src/modules/settings/components/molecules/integrations/integration-connection-badges';
import { IntegrationHealthAlert } from 'src/modules/settings/components/molecules/integrations/integration-health-alert';
import { IntegrationSyncSummary } from 'src/modules/settings/components/molecules/integrations/integration-sync-summary';
import { IntegrationCard } from 'src/modules/settings/components/molecules/integrations/integration-card';
import { IntegrationActionsMenu } from 'src/modules/settings/components/molecules/integrations/integration-actions-menu';
import { ButtonSkeleton } from 'src/modules/shared/components/atoms/buttons/button-skeleton';
import {
  buildPropertyImportOptions,
  DEFAULT_IMPORT_PROPERTY_SCOPE,
  DEFAULT_IMPORT_SYNC_MISSING_POLICY,
  isAutoAssignmentEnabledFromConfig,
  resolveImportPropertyScope,
  resolveImportSyncMissingPolicy,
} from 'src/modules/settings/utils/integration-import-options.util';
import { IntegrationBrandAvatar } from '../../molecules/integrations/integration-brand-avatar';

const PROVIDER_ID = '2clics' as IntegrationProviderId;

const hasCredentialId = (
  credential: TCCredential
): credential is TCCredential & { credentialId: string } =>
  typeof credential.credentialId === 'string' &&
  credential.credentialId.length > 0;

// eslint-disable-next-line sonarjs/cognitive-complexity
export const TwoClicsIntegrationCard: React.FC = () => {
  const { t, language } = useI18n();
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const { hasPermission: canRead } = useHasPermissions(
    IntegrationPermission.Read
  );
  const { hasPermission: canUpdate } = useHasPermissions(
    IntegrationPermission.Update
  );
  const { hasPermission: canDelete } = useHasPermissions(
    IntegrationPermission.Delete
  );
  const { hasPermission: canSyncNow } = useHasPermissions(
    IntegrationPermission.SyncNow
  );

  const {
    integrations,
    loading: loadingIntegrations,
    refetch: refetchIntegrations,
  } = useIntegrations(canRead);

  const isInitialLoading = canRead && (loadingIntegrations || !integrations);

  const connection = useMemo(() => {
    const item =
      integrations?.items.find((i) => i.providerId === PROVIDER_ID) ?? null;
    return item?.connection ?? null;
  }, [integrations]);

  const uiStatus: IntegrationConnectionUiStatus =
    connection?.status ?? 'disconnected';

  const isConnected = uiStatus === IntegrationConnectionStatus.ACTIVE;
  const healthStatus =
    connection?.healthStatus ?? IntegrationConnectionHealthStatus.HEALTHY;
  const isHealthError =
    healthStatus === IntegrationConnectionHealthStatus.ERROR;
  const isHealthPaused =
    healthStatus === IntegrationConnectionHealthStatus.PAUSED;
  const isHealthSyncing =
    healthStatus === IntegrationConnectionHealthStatus.SYNCING;
  const isEditBlockedWhileSyncing = Boolean(connection) && isHealthSyncing;

  const { credentials, loading: loadingCredentials } = useCredentials({
    provider: PROVIDER_ID,
    kind: TCCredentialKind.API_KEY,
    limit: 200,
    enabled: canRead,
  });

  const credentialOptions = useMemo(() => {
    const base = [
      {
        value: '',
        content: t('settings.integrations.2clics.credential.select'),
      },
    ];
    return base.concat(
      credentials
        .filter((c) => c.status === TCCredentialStatus.ACTIVE)
        .filter(hasCredentialId)
        .map((credential) => ({
          value: credential.credentialId,
          content: `${credential.name ?? '-'} (${credential.credentialId})`,
        }))
    );
  }, [credentials, t]);

  const [credentialId, setCredentialId] = useState<string>('');
  const [periodicSyncEnabled, setPeriodicSyncEnabled] = useState<boolean>(true);
  const [syncFrequencyMinutes, setSyncFrequencyMinutes] = useState<number>(60);
  const [autoAssignEnabled, setAutoAssignEnabled] = useState<boolean>(false);
  const [syncMissingPolicy, setSyncMissingPolicy] =
    useState<TCPropertyImportSyncMissingPolicy>(
      DEFAULT_IMPORT_SYNC_MISSING_POLICY
    );
  const [propertyScope, setPropertyScope] =
    useState<TCPropertyImportPropertyScope>(DEFAULT_IMPORT_PROPERTY_SCOPE);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] =
    useState<boolean>(false);
  const minSyncFrequencyMinutes = useMemo(
    () => getIntegrationMinSyncFrequencyMinutes(PROVIDER_ID),
    []
  );

  useEffect(() => {
    if (!connection) {
      setCredentialId('');
      setPeriodicSyncEnabled(true);
      setSyncFrequencyMinutes(Math.max(60, minSyncFrequencyMinutes));
      setAutoAssignEnabled(false);
      setSyncMissingPolicy(DEFAULT_IMPORT_SYNC_MISSING_POLICY);
      setPropertyScope(DEFAULT_IMPORT_PROPERTY_SCOPE);
      return;
    }

    setCredentialId(connection.credentialId ?? '');
    const config = connection.config ?? null;
    setAutoAssignEnabled(isAutoAssignmentEnabledFromConfig(config));
    setSyncMissingPolicy(resolveImportSyncMissingPolicy(config));
    setPropertyScope(resolveImportPropertyScope(config));
    const syncFrequency = connection.syncFrequencyMinutes;
    const enabled = typeof syncFrequency === 'number';
    setPeriodicSyncEnabled(enabled);
    setSyncFrequencyMinutes(
      enabled
        ? Math.max(syncFrequency, minSyncFrequencyMinutes)
        : minSyncFrequencyMinutes
    );
  }, [connection, minSyncFrequencyMinutes]);

  useEffect(() => {
    if (periodicSyncEnabled && syncFrequencyMinutes < minSyncFrequencyMinutes) {
      setSyncFrequencyMinutes(minSyncFrequencyMinutes);
    }
  }, [periodicSyncEnabled, syncFrequencyMinutes, minSyncFrequencyMinutes]);

  const { connectIntegration, loading: connecting } = useConnectIntegration();
  const { disconnectIntegration, loading: disconnecting } =
    useDisconnectIntegration();
  const { enqueueIntegrationSync, loading: syncing } =
    useEnqueueIntegrationSync();

  const navigateToCredentials = useCallback(() => {
    void navigate({
      to: SettingsCredentialsRoute.to,
      search: (prev) => settingsSearchSchema.parse(prev),
    });
  }, [navigate]);

  const canSubmitConnect = canUpdate && credentialId.trim().length > 0;
  const canSubmitWhenSyncing = !isEditBlockedWhileSyncing;

  const missingPolicyOptions = useMemo(() => {
    return [
      {
        value: TCPropertyImportSyncMissingPolicy.UPSERT,
        content: t(
          'settings.integrations.2clics.syncMissingPolicy.option.upsert'
        ),
      },
      {
        value: TCPropertyImportSyncMissingPolicy.REPLACE,
        content: t(
          'settings.integrations.2clics.syncMissingPolicy.option.replace'
        ),
      },
    ];
  }, [t]);

  const propertyScopeOptions = useMemo(() => {
    return [
      {
        value: TCPropertyImportPropertyScope.ALL,
        content: t('settings.integrations.2clics.propertyScope.option.all'),
      },
      {
        value: TCPropertyImportPropertyScope.ACTIVE,
        content: t('settings.integrations.2clics.propertyScope.option.active'),
      },
    ];
  }, [t]);

  const handleConnect = async () => {
    if (isEditBlockedWhileSyncing) {
      showAlert(
        t('settings.integrations.sync.editBlockedWhileSyncing'),
        'warning'
      );
      return;
    }

    const importOptions = buildPropertyImportOptions({
      autoAssignmentEnabled: autoAssignEnabled,
      syncMissingPolicy,
      propertyScope,
    });

    await connectIntegration({
      providerId: PROVIDER_ID,
      body: {
        credentialId,
        config: {
          importOptions,
        },
        syncFrequencyMinutes: periodicSyncEnabled ? syncFrequencyMinutes : null,
        status: IntegrationConnectionStatus.ACTIVE,
      },
    });
  };

  const handleDisconnect = async () => {
    try {
      await disconnectIntegration({ providerId: PROVIDER_ID });
      setIsDisconnectModalOpen(false);
      showAlert(
        t('settings.integrations.2clics.disconnect.success'),
        'success'
      );
    } catch (error) {
      console.error('Error disconnecting TwoClics integration:', error);
      showAlert(t('settings.integrations.2clics.disconnect.error'), 'error');
    }
  };

  const handleSync = useCallback(async () => {
    try {
      await enqueueIntegrationSync({
        providerId: PROVIDER_ID,
        body: {},
      });
    } catch (error) {
      const message = extractApiErrorMessage(error);
      const isSyncAlreadyInProgress = message
        .toLowerCase()
        .includes('sync already in progress');

      showAlert(message, isSyncAlreadyInProgress ? 'warning' : 'error');

      if (isSyncAlreadyInProgress) {
        await refetchIntegrations();
      }
    }
  }, [enqueueIntegrationSync, refetchIntegrations, showAlert]);

  const lastSyncSuccessAt =
    connection?.lastSyncSuccessAt ?? connection?.lastSyncAt ?? null;
  const lastSyncAttemptAt =
    connection?.lastSyncAttemptAt ?? connection?.lastSyncAt ?? null;
  const lastErrorMessage =
    typeof connection?.lastErrorMessage === 'string'
      ? connection.lastErrorMessage.trim()
      : '';
  const pausedUntil = connection?.pausedUntil ?? null;
  const canSyncNowConnection =
    canSyncNow && Boolean(connection) && isConnected && !isHealthError;
  const hasLastErrorMessage = lastErrorMessage.length > 0;
  const notAvailableLabel = t('settings.integrations.notAvailable');
  const errorAlertMessage = hasLastErrorMessage
    ? t('settings.integrations.2clics.health.error.messageWithDetails', {
        message: lastErrorMessage,
      })
    : t('settings.integrations.2clics.health.error.message');
  const pausedAlertMessage = hasLastErrorMessage
    ? t('settings.integrations.2clics.health.paused.messageWithDetails', {
        message: lastErrorMessage,
      })
    : t('settings.integrations.2clics.health.paused.message');

  const actionItems = useMemo(() => {
    const items = [];

    if (canSyncNowConnection) {
      items.push({
        content: t('settings.integrations.2clics.actions.syncNow'),
        icon: <PiArrowClockwise className="w-4 h-4" />,
        disabled: syncing || isHealthSyncing,
        onClick: () => {
          if (syncing || isHealthSyncing) return;
          void handleSync();
        },
      });
    }

    if (canDelete && connection) {
      items.push({
        content: t('settings.integrations.2clics.actions.disconnect'),
        icon: <PiLinkBreak className="w-4 h-4" />,
        disabled: disconnecting,
        onClick: () => {
          if (disconnecting) return;
          setIsDisconnectModalOpen(true);
        },
        separator: true,
      });
    }

    return items;
  }, [
    canDelete,
    canSyncNowConnection,
    connection,
    disconnecting,
    handleSync,
    isHealthSyncing,
    syncing,
    t,
  ]);

  const syncMetadata = useMemo((): {
    labelKey: TranslationKey;
    value: string;
  }[] => {
    const locale = language;
    const metadata: { labelKey: TranslationKey; value: string }[] = [
      {
        labelKey: 'settings.integrations.2clics.sync.lastSuccess',
        value: formatIntegrationDateTime(lastSyncSuccessAt, {
          locale,
          notAvailableLabel,
        }),
      },
      {
        labelKey: 'settings.integrations.2clics.sync.lastAttempt',
        value: formatIntegrationDateTime(lastSyncAttemptAt, {
          locale,
          notAvailableLabel,
        }),
      },
      {
        labelKey: 'settings.integrations.2clics.sync.next',
        value: formatIntegrationNextSyncDateTime(connection?.nextSyncAt, {
          locale,
          notAvailableLabel,
          startingSoonLabel: t('settings.integrations.sync.startingSoon'),
          startingSoonGraceMinutes: 15,
        }),
      },
    ];

    if (pausedUntil) {
      metadata.push({
        labelKey: 'settings.integrations.2clics.sync.pausedUntil',
        value: formatIntegrationDateTime(pausedUntil, {
          locale,
          notAvailableLabel,
        }),
      });
    }

    if (hasLastErrorMessage) {
      metadata.push({
        labelKey: 'settings.integrations.2clics.sync.lastError',
        value: lastErrorMessage,
      });
    }

    return metadata;
  }, [
    connection?.nextSyncAt,
    hasLastErrorMessage,
    language,
    lastErrorMessage,
    lastSyncAttemptAt,
    lastSyncSuccessAt,
    notAvailableLabel,
    pausedUntil,
    t,
  ]);

  return (
    <>
      <IntegrationCard
        title={t('settings.integrations.2clics.title')}
        subtitle={t('settings.integrations.2clics.subtitle')}
        cardProps={{ shadow: 'md', variant: 'primary', appearance: 'outline' }}
        icon={<IntegrationBrandAvatar integrationId="2clics" />}
        badges={
          <IntegrationConnectionBadges
            status={uiStatus}
            healthStatus={connection ? healthStatus : null}
            t={t}
          />
        }
        meta={
          connection ? (
            <IntegrationSyncSummary items={syncMetadata} t={t} />
          ) : null
        }
        rightAction={
          canRead ? (
            isInitialLoading ? (
              <div className="flex items-center justify-end gap-2 flex-wrap">
                <ButtonSkeleton size="md" variant="neutral" withIcon />
                <div className="h-9 w-10 rounded-lg border border-gray-200 bg-neutral-200 animate-pulse" />
              </div>
            ) : (
              <div className="flex items-center justify-end gap-2 flex-wrap">
                <Tooltip
                  content={
                    isEditBlockedWhileSyncing
                      ? t('settings.integrations.sync.editBlockedWhileSyncing')
                      : isConnected
                        ? t(
                            'settings.integrations.2clics.actions.saveChanges.tooltip'
                          )
                        : t(
                            'settings.integrations.2clics.actions.connect.tooltip'
                          )
                  }
                  placement="top"
                  size="sm"
                  as="span"
                >
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleConnect}
                    loading={connecting}
                    disabled={
                      !canSubmitConnect ||
                      !canSubmitWhenSyncing ||
                      connecting ||
                      loadingIntegrations
                    }
                    className="flex items-center gap-2"
                  >
                    <PiLink className="w-4 h-4" />
                    {isConnected
                      ? t('settings.integrations.2clics.actions.saveChanges')
                      : t('settings.integrations.2clics.actions.connect')}
                  </Button>
                </Tooltip>

                {isConnected ? (
                  <IntegrationActionsMenu
                    ariaLabel="Actions"
                    disabled={disconnecting || loadingIntegrations}
                    loading={syncing || disconnecting}
                    items={actionItems}
                  />
                ) : null}
              </div>
            )
          ) : null
        }
      >
        {!canRead ? (
          <Alert
            variant="warning"
            size="md"
            message={t('settings.integrations.forbidden')}
          />
        ) : (
          <div className="flex flex-col gap-6">
            {isHealthError ? (
              <IntegrationHealthAlert
                variant="danger"
                message={errorAlertMessage}
                t={t}
                cta={{
                  labelKey: 'settings.integrations.2clics.health.error.cta',
                  onClick: navigateToCredentials,
                  visible: canUpdate,
                }}
              />
            ) : null}

            {isHealthPaused ? (
              <IntegrationHealthAlert
                variant="warning"
                message={pausedAlertMessage}
                t={t}
              />
            ) : null}

            {credentials.length === 0 && !loadingCredentials ? (
              <Alert
                variant="info"
                size="md"
                message={t('settings.integrations.2clics.noCredentials')}
              />
            ) : null}

            <div className="flex flex-col gap-1">
              <div className="flex items-end gap-2">
                <div className="flex-1 min-w-0">
                  <LabelSelect
                    label={t('settings.integrations.2clics.credential.label')}
                    labelPosition="top"
                    options={credentialOptions}
                    selectProps={{
                      value: credentialId,
                      onChange: (e) => setCredentialId(e.target.value),
                      disabled:
                        !canUpdate ||
                        loadingCredentials ||
                        isEditBlockedWhileSyncing,
                    }}
                  />
                </div>

                <Tooltip
                  content={t(
                    'settings.integrations.2clics.credential.actions.create.tooltip'
                  )}
                  placement="top"
                  size="sm"
                  as="span"
                >
                  <Button
                    variant="primary"
                    appearance="soft"
                    size="md"
                    isIconOnly
                    onClick={navigateToCredentials}
                  >
                    <PiPlus className="w-4 h-4" />
                  </Button>
                </Tooltip>
              </div>
              <Text size="tiny" color="secondary">
                {t('settings.integrations.2clics.credential.help')}
              </Text>
            </div>

            <LabelSwitch
              label={t('settings.integrations.2clics.autoAssign.label')}
              checked={autoAssignEnabled}
              onChange={setAutoAssignEnabled}
              disabled={!canUpdate}
              labelPosition="top"
              helperText={t('settings.integrations.2clics.autoAssign.help')}
            />

            <div className="space-y-1">
              <LabelSelect
                label={t(
                  'settings.integrations.2clics.syncMissingPolicy.label'
                )}
                labelPosition="top"
                options={missingPolicyOptions}
                selectProps={{
                  value: syncMissingPolicy,
                  onChange: (e) =>
                    setSyncMissingPolicy(
                      e.target.value as TCPropertyImportSyncMissingPolicy
                    ),
                  disabled: !canUpdate,
                }}
              />
              <Text size="tiny" color="secondary">
                {t('settings.integrations.2clics.syncMissingPolicy.help')}
              </Text>
            </div>

            <div className="space-y-1">
              <LabelSelect
                label={t('settings.integrations.2clics.propertyScope.label')}
                labelPosition="top"
                options={propertyScopeOptions}
                selectProps={{
                  value: propertyScope,
                  onChange: (e) =>
                    setPropertyScope(
                      e.target.value as TCPropertyImportPropertyScope
                    ),
                  disabled: !canUpdate,
                }}
              />
              <Text size="tiny" color="secondary">
                {t('settings.integrations.2clics.propertyScope.help')}
              </Text>
            </div>

            <LabelSwitch
              label={t(
                periodicSyncEnabled
                  ? 'settings.integrations.2clics.autoSync.label.enabled'
                  : 'settings.integrations.2clics.autoSync.label.paused'
              )}
              checked={periodicSyncEnabled}
              onChange={setPeriodicSyncEnabled}
              disabled={!canUpdate || isEditBlockedWhileSyncing}
              labelPosition="top"
            />

            <LabelNumberInput
              label={t('settings.integrations.2clics.syncFrequency.label')}
              value={syncFrequencyMinutes}
              onChange={(value) => setSyncFrequencyMinutes(value)}
              min={minSyncFrequencyMinutes}
              max={24 * 60}
              disabled={
                !canUpdate || !periodicSyncEnabled || isEditBlockedWhileSyncing
              }
              helperText={t('settings.integrations.2clics.syncFrequency.help', {
                min: minSyncFrequencyMinutes,
              })}
            />
          </div>
        )}
      </IntegrationCard>

      <Modal
        isOpen={isDisconnectModalOpen}
        onClose={() => {
          if (!disconnecting) setIsDisconnectModalOpen(false);
        }}
        header={
          <Text size="h6" weight="semibold">
            {t('settings.integrations.2clics.disconnectDialog.title')}
          </Text>
        }
        body={
          <div className="w-full space-y-2">
            <Text size="paragraph">
              {t('settings.integrations.2clics.disconnectDialog.description')}
            </Text>
          </div>
        }
        footer={
          <div className="flex justify-end gap-3 px-4 py-3">
            <Button
              variant="neutral"
              appearance="ghost"
              onClick={() => setIsDisconnectModalOpen(false)}
              disabled={disconnecting}
            >
              {t('settings.integrations.2clics.disconnectDialog.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => void handleDisconnect()}
              loading={disconnecting}
              disabled={disconnecting}
            >
              {t('settings.integrations.2clics.disconnectDialog.confirm')}
            </Button>
          </div>
        }
        dialogClassName="max-w-lg"
      />
    </>
  );
};
